import { TrendingUp, TrendingDown, Zap } from 'lucide-react';

/**
 * Mobile-optimized anomaly card component
 * Designed for 44px+ tap targets and clean visual hierarchy
 */
const MobileAnomalyCard = ({ anomaly, onClick, isNew, index }) => {
  const isPositive = anomaly.daily_return >= 0;
  const score = anomaly.unusual_activity_score || 0;
  
  // Confidence badge based on score
  const getConfidenceBadge = (score) => {
    if (score >= 70) return { label: 'High', color: '#f87171', bg: 'rgba(248, 113, 113, 0.15)' };
    if (score >= 50) return { label: 'Medium', color: '#fbbf24', bg: 'rgba(251, 191, 36, 0.15)' };
    return { label: 'Low', color: '#4ade80', bg: 'rgba(74, 222, 128, 0.15)' };
  };
  
  const confidence = getConfidenceBadge(score);
  
  // Check if anomaly is fresh (< 30 mins old)
  const isFresh = () => {
    if (!anomaly.detected_at) return false;
    const detectedTime = new Date(anomaly.detected_at);
    const now = new Date();
    const diffMins = (now - detectedTime) / (1000 * 60);
    return diffMins < 30;
  };
  
  const fresh = isFresh();
  
  return (
    <button
      onClick={() => onClick(anomaly.symbol)}
      className="w-full text-left active:scale-[0.98] transition-transform"
      style={{ minHeight: '72px' }}
      data-testid={`mobile-anomaly-${anomaly.symbol}`}
    >
      <div 
        className="p-4 rounded-xl transition-all"
        style={{ 
          backgroundColor: 'rgba(15, 23, 42, 0.6)',
          border: '1px solid rgba(255,255,255,0.06)',
        }}
      >
        <div className="flex items-center justify-between gap-3">
          {/* Left: Symbol + Company */}
          <div className="flex items-center gap-3 min-w-0 flex-1">
            {/* Fresh indicator */}
            {fresh && (
              <div 
                className="w-2 h-2 rounded-full flex-shrink-0"
                style={{ 
                  backgroundColor: '#22C55E',
                  boxShadow: '0 0 8px rgba(34, 197, 94, 0.6)',
                  animation: 'pulse-glow 2s ease-in-out infinite'
                }}
              />
            )}
            
            <div className="min-w-0">
              <div className="flex items-center gap-2">
                <span className="font-bold text-base" style={{ color: '#F8FAFC' }}>
                  {anomaly.symbol}
                </span>
                {isNew && (
                  <span 
                    className="text-[10px] font-semibold px-1.5 py-0.5 rounded"
                    style={{ backgroundColor: 'rgba(59, 130, 246, 0.2)', color: '#60A5FA' }}
                  >
                    NEW
                  </span>
                )}
              </div>
              {anomaly.company_name && anomaly.company_name !== anomaly.symbol && (
                <p 
                  className="text-xs truncate mt-0.5" 
                  style={{ color: '#64748B', maxWidth: '140px' }}
                >
                  {anomaly.company_name}
                </p>
              )}
            </div>
          </div>
          
          {/* Center: Price Change */}
          <div className="flex items-center gap-1.5">
            {isPositive ? (
              <TrendingUp className="h-4 w-4" style={{ color: '#4ade80' }} />
            ) : (
              <TrendingDown className="h-4 w-4" style={{ color: '#f87171' }} />
            )}
            <span 
              className="font-mono font-bold text-base"
              style={{ color: isPositive ? '#4ade80' : '#f87171' }}
            >
              {isPositive ? '+' : ''}{anomaly.daily_return?.toFixed(1)}%
            </span>
          </div>
          
          {/* Right: Indicators */}
          <div className="flex items-center gap-2 flex-shrink-0">
            {/* Volume indicator */}
            {anomaly.volume_multiple >= 2 && (
              <div 
                className="flex items-center gap-1 px-2 py-1 rounded-md"
                style={{ backgroundColor: 'rgba(251, 191, 36, 0.1)' }}
              >
                <Zap className="h-3 w-3" style={{ color: '#fbbf24' }} />
                <span className="text-xs font-medium" style={{ color: '#fbbf24' }}>
                  {anomaly.volume_multiple?.toFixed(1)}x
                </span>
              </div>
            )}
            
            {/* Confidence badge */}
            <span 
              className="text-xs font-medium px-2 py-1 rounded-md"
              style={{ backgroundColor: confidence.bg, color: confidence.color }}
            >
              {confidence.label}
            </span>
          </div>
        </div>
      </div>
      
      <style>{`
        @keyframes pulse-glow {
          0%, 100% { opacity: 1; transform: scale(1); }
          50% { opacity: 0.6; transform: scale(1.2); }
        }
      `}</style>
    </button>
  );
};

export default MobileAnomalyCard;
